import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import '../css/PaymentCancel.css';

const PaymentCancel = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [updateComplete, setUpdateComplete] = useState(false);
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [programDetails, setProgramDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Base URL for API calls
  const baseUrl = process.env.REACT_APP_NODE_ENV === 'production' 
    ? process.env.REACT_APP_PRODUCTION_BASE_URL 
    : process.env.REACT_APP_DEVELOPMENT_BASE_URL;

  useEffect(() => {
    // Try to get payment details from session storage
    try {
      const storedDetails = window.sessionStorage.getItem('paymentDetails');
      if (storedDetails) {
        const details = JSON.parse(storedDetails);
        setPaymentDetails(details);
        
        // Fetch program details if we have programId
        if (details.programId) {
          fetchProgramDetails(details.programId);
        } else {
          setLoading(false);
        }
        
        // Update payment status to 'canceled' in database
        updatePaymentStatus(details.userId);
      } else {
        setLoading(false);
      }
    } catch (err) {
      console.error('Failed to parse payment details from session storage:', err);
      setLoading(false);
    }
  }, []);

  const fetchProgramDetails = async (programId) => {
    try {
      const response = await axios.get(`${baseUrl}/api/course/${programId}`);
      if (response.data.success) {
        setProgramDetails(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching program details:', err);
    } finally {
      setLoading(false);
    }
  };

  const updatePaymentStatus = async (userId) => {
    if (!userId || updateComplete) return;
    
    try {
      const response = await axios.post(
        `${baseUrl}/api/user/update-payment-status/update`,
        {
          userId: userId,
          paymentStatus: 'canceled'
        }
      );
      
      if (response.data.success) {
        // Update local payment details
        if (paymentDetails) {
          const updatedDetails = {
            ...paymentDetails,
            paymentStatus: 'canceled'
          };
          window.sessionStorage.setItem('paymentDetails', JSON.stringify(updatedDetails));
          setPaymentDetails(updatedDetails);
        }
        setUpdateComplete(true);
      }
    } catch (err) {
      console.error('Failed to update payment status:', err);
    }
  };

  const handleTryAgain = () => {
    // Clear existing payment attempt data but keep user and program info
    if (paymentDetails) {
      const resetDetails = {
        ...paymentDetails,
        paymentStatus: 'pending',
        paymentId: null,
        timestamp: new Date().toISOString()
      };
      window.sessionStorage.setItem('paymentDetails', JSON.stringify(resetDetails));
      
      // Construct query parameters with all necessary details
      const queryParams = new URLSearchParams();
      if (paymentDetails.paymentMethod) {
        queryParams.set('method', paymentDetails.paymentMethod);
      }
      if (paymentDetails.amount) {
        queryParams.set('amount', paymentDetails.amount);
      }
      if (paymentDetails.userId) {
        queryParams.set('userId', paymentDetails.userId);
      }
      if (paymentDetails.programId) {
        queryParams.set('programId', paymentDetails.programId);
      }
      if (paymentDetails.programName) {
        queryParams.set('programName', paymentDetails.programName);
      }
      
      // Navigate to payment instructions with query parameters
      navigate(`/payment-instructions?${queryParams.toString()}`);
    } else {
      // If no payment details, just navigate to payment instructions
      navigate('/payment-instructions');
    }
  };

  if (loading) {
    return (
      <div className="payment-cancel-page loading">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p>Loading payment information...</p>
      </div>
    );
  }

  return (
    <section className="payment-cancel-section">
      <div className="container">
        <div className="row">
          <div className="col-lg-6 mb-3 mb-md-0">
            <div className="position-stick-top">
              <h4>Payment Cancelled</h4>
              <div className="cancel-summary mb-4">
                <div className="cancel-icon">
                  <i className="fa fa-times-circle"></i>
                </div>
                <p className="cancel-message">
                  Your payment process has been cancelled. No charges have been made to your account.
                </p>
                {paymentDetails && programDetails && (
                  <div className="payment-info">
                    <div className="detail-row">
                      <span className="detail-label">Program:</span>
                      <span className="detail-value">{programDetails.programName || paymentDetails.programName}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Amount:</span>
                      <span className="detail-value">${Number(paymentDetails.amount || programDetails.programPrice).toFixed(2)} CAD</span>
                    </div>
                  </div>
                )}
              </div>
              <button onClick={handleTryAgain} className="btn1">
                Try Payment Again
              </button>
            </div>
          </div>

          <div className="col-lg-6">
            <div className="content">
              <div className="instruction-card">
                <div className="card-header">
                  <i className="fa fa-info-circle"></i>
                  <h5>Available Options</h5>
                </div>
                <div className="card-body">
                  <div className="help-options">
                    <div className="help-item">
                      <i className="fa fa-credit-card"></i>
                      <span>Try a different payment method</span>
                    </div>
                    <div className="help-item">
                      <i className="fa fa-clock"></i>
                      <span>Complete payment later</span>
                    </div>
                    <div className="help-item">
                      <i className="fa fa-calendar"></i>
                      <span>Schedule a payment plan</span>
                    </div>
                    <div className="help-item">
                      <i className="fa fa-headset"></i>
                      <span>Get assistance from our support team</span>
                    </div>
                  </div>

                  <div className="contact-support">
                    <div className="support-item">
                      <i className="fa fa-envelope"></i>
                      <a href="mailto:support@confidentdriver.com">support@confidentdriver.com</a>
                    </div>
                    <div className="support-item">
                      <i className="fa fa-phone"></i>
                      <a href="tel:+19054598484">+1 905-459-8484</a>
                    </div>
                  </div>
                </div>
              </div>

              <div className="action-buttons">
                <Link to="/" className="btn btn-outline">Return Home</Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PaymentCancel; 